import { guards } from '@shared/helpers/typeguards.helpers';

const UNKNOWN_WORKER_ERROR = 'Unknown worker error during enrichment';

export type WorkerErrorEventLike = Pick<
  ErrorEvent,
  'message' | 'error' | 'filename' | 'lineno' | 'colno'
>;

export type WorkerErrorRejectionDecision =
  { kind: 'skip' } | { kind: 'reject'; error: Error; shouldResetWorker: true };

/**
 * Build a descriptive {@link Error} from a worker {@link ErrorEvent}.
 * Browser `message` is often empty even when `error`, `filename`, or line info exist.
 */
export const formatWorkerErrorFromEvent = (
  event: WorkerErrorEventLike,
): Error => {
  const underlying = event.error;

  if (guards.is.error(underlying) && underlying.message) {
    return underlying;
  }

  const location =
    event.filename && event.lineno
      ? `${event.filename}:${event.lineno}${event.colno ? `:${event.colno}` : ''}`
      : undefined;

  const detailParts = [
    event.message,
    underlying != null ? String(underlying) : undefined,
    location,
  ].filter((part) => Boolean(part && part.trim()));

  const message = detailParts.join(' — ') || UNKNOWN_WORKER_ERROR;
  const error = new Error(message);

  if (guards.is.error(underlying)) {
    error.name = underlying.name;
    if (underlying.stack) error.stack = underlying.stack;
  }

  return error;
};

/**
 * True when the worker event carries no actionable error payload.
 * Comlink may still reject the in-flight call with the real error.
 */
export const isEmptyWorkerErrorEvent = (
  event: Pick<ErrorEvent, 'message' | 'error' | 'filename' | 'lineno'>,
): boolean =>
  !event.message?.trim() &&
  event.error == null &&
  !event.filename &&
  event.lineno === 0;

/**
 * Decide whether a deferred `worker.onerror` handler should reject the in-flight enrichment.
 * Runs after {@link queueMicrotask} so Comlink can settle the promise with the real API error.
 */
export const resolveDeferredWorkerErrorRejection = (
  event: WorkerErrorEventLike,
  rejectInFlightStillBound: boolean,
): WorkerErrorRejectionDecision => {
  if (!rejectInFlightStillBound) return { kind: 'skip' };
  if (isEmptyWorkerErrorEvent(event)) return { kind: 'skip' };

  return {
    kind: 'reject',
    error: formatWorkerErrorFromEvent(event),
    shouldResetWorker: true,
  };
};
